import React from "react";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { currency_symbol, offeringLimitNote, scheduledFirstChargeNote, weekdayRestrictionLabel } from "../NewOffering/utils";
import { calculateTotalSessions } from "app/components/helpers/CalculateTotalSessions";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { formatServerAmount } from "app/utils/currency";

interface PackageDetailsModalProps {
  product: {
    title: string;
    description: string;
    price: number;
    currency: string;
    enabled: boolean;
    productType: "product" | "subscription";
    recurring?: string;
    restrictWeekdays?: boolean;
    restrictWeekends?: boolean;
    weekdayBeginAt?: number;
    weekdayEndAt?: number;
    // CFTB-3419: the configured limit the offering cards show — the details
    // view is where an athlete goes for the full picture, so it can't be the
    // one surface that omits it.
    expiresInDays?: number | null;
    maxBillingCycles?: number | null;
    // CN-2832: a custom start date bills nothing at checkout.
    billStartDate?: string | null;
    billingStartOn?: string | null;
    rateLinks: {
      duration: string;
      title: string;
      quantity: number | null;
    }[];
  };
  isOpen: boolean;
  onClose: () => void;
  buttonTitle: string;
  /**
   * CFTB-3445: for a state rather than an action — "Subscribed" on a subscription
   * the athlete already holds. Omitted everywhere else, so the button stays live.
   */
  buttonDisabled?: boolean;
  onButtonClick: () => void;
}

export default function PackageDetailsModal({
  product,
  isOpen,
  onClose,
  buttonTitle,
  buttonDisabled = false,
  onButtonClick,
}: PackageDetailsModalProps) {
  const { t } = useTranslation();
  const totalSessions = calculateTotalSessions(product);
  const limitNote = offeringLimitNote(product, t);
  const firstChargeNote = scheduledFirstChargeNote(
    product,
    `${currency_symbol[product.currency] ?? ""}${formatServerAmount(product.price)}`,
    t,
  );

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      classNames={`${styles.modal}`}
      wrapperClassName={styles.wrapper}
    >
      <div className={styles.modal_container}>
        <div className={styles.modalHeader}>
          <span
            className={`
            ${product.enabled ? styles.packageLabel : styles.inactiveLabel}
            ${product.productType === "product" ? styles.product_color : styles.subscription_color}
          `}
          >
            {t(product.productType === "product" ? "PACKAGE" : "SUBSCRIPTION")}
          </span>
          <button
            type="button"
            className={styles.close_button}
            onClick={onClose}
            aria-label={t("Close")}
          >
            <i className="ico ico-x" />
          </button>
        </div>
        <div className={styles.modalTitle}>{product.title}</div>
        <div className={styles.body}>
          <div className={styles.contentWrapper}>
            <div className={styles.leftColumn}>
              <p className={styles.description}>{product.description}</p>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.packageBreakdown}>
                <i className={`ico ico-feed-o ${styles.icon_details}`} />
                <strong className={styles.breakdownTitle}>
                  {t("This package includes")}
                </strong>
                <ul>
                  {product.rateLinks?.map((rate, idx) => {
                    if (!rate.title) {
                      return null;
                    }
                    const isUnlimited = rate.quantity == null;
                    return (
                      <li key={idx} className={styles.sessionRow}>
                        <span className={styles.sessionTitle}>
                          {rate.title}
                        </span>
                        <span className={styles.count}>
                          {isUnlimited ? (
                            <span className={styles.infinitySymbol}>
                              &infin;
                            </span>
                          ) : (
                            rate.quantity
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <hr />
                <div className={styles.totalRow}>
                  <span className={styles.totalRowSessions}>
                    {t("Total Sessions")}
                  </span>
                  <span>
                    {totalSessions ?? (
                      <span className={styles.infinitySymbol}>&infin;</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {product.restrictWeekdays && !product.restrictWeekends && (
            <div className={styles.restriction_info}>
              <span className={styles.restriction_badge}>
                {weekdayRestrictionLabel(
                  product.weekdayBeginAt ?? 0,
                  product.weekdayEndAt ?? 1440,
                  t("Available: Weekdays only (Mon-Fri)"),
                )}
              </span>
            </div>
          )}
          {product.restrictWeekends && !product.restrictWeekdays && (
            <div className={styles.restriction_info}>
              <span className={styles.restriction_badge}>
                {t("Available: Weekends & Holidays only (Sat-Sun)")}
              </span>
            </div>
          )}
          {limitNote && <p className={styles.config_note}>{limitNote}</p>}
          {firstChargeNote && <p className={styles.config_note}>{firstChargeNote}</p>}
        </div>
        <div className={styles.footerWrapper}>
          <hr className={styles.divider} />

          <div className={styles.footer}>
            <div className={styles.price}>
              <strong>
                {currency_symbol[product.currency]} {formatServerAmount(product.price)}
              </strong>
              <div className={styles.billing}>
                {product?.productType === "product"
                  ? t("One time payment")
                  : product.recurring === "1"
                    ? t("Billed monthly")
                    : product.recurring === "12"
                      ? t("Billed annually")
                      : t("Billed every {{count}} months", {
                          count: Number(product.recurring),
                        })}
              </div>
            </div>
            <ButtonVariant
              title={t(buttonTitle)}
              buttonType="primary"
              disabled={buttonDisabled}
              onClickFunc={onButtonClick}
            />
          </div>
        </div>{" "}
      </div>
    </ModalLayout>
  );
}
